/**
 * Marketing → SyncFX app: the Home card feed and the Help screen's video list,
 * which the SyncFX app reads from https://syncfxapp.com/app/.
 *
 * The source of truth is the Chauvetlighting/SYNCFX_WEB_SERVER repo, not ATLAS.
 * ATLAS keeps no copy: it reads `app/feed.json` and `app/helpvideos.json` from
 * GitHub when the page opens and publishes an edit as a pull request, which
 * merges itself once the repo's `tools/check-feed` passes.
 *
 * The rules below mirror that script (and through it the app's parseFeed()), so
 * the form can say what is wrong before a publish rather than after a red check.
 * check-feed stays the final gate: when the app learns a new field, it learns it
 * first, and this file has to follow.
 */
import { navFeatureId } from './feature-visibility.js';
export const SYNCFX_CONTENT_PATH = '/marketing/syncfx';
export const SYNCFX_CONTENT_FEATURE = navFeatureId(SYNCFX_CONTENT_PATH);
/** Where the app reads from. A picture committed in the repo's app/ folder is served here. */
export const SYNCFX_APP_BASE_URL = 'https://syncfxapp.com/app/';
export const SYNCFX_FEED_TYPES = ['promo', 'feature', 'video', 'release', 'notice'];
export const SYNCFX_FEED_TYPE_LABELS = {
    promo: 'Promo',
    feature: 'Feature tip',
    video: 'Video',
    release: 'Release',
    notice: 'Notice',
};
/**
 * How a card is drawn (SYNCFX_WEB_SERVER #23). Left out: the standard card.
 * "image": the picture is the whole card, and tapping it opens the link or
 * video; no text or button is drawn. "image-button": the picture plus the
 * button, bottom left. Both need an image; any type can use either.
 */
export const SYNCFX_FEED_LAYOUTS = ['image', 'image-button'];
export const SYNCFX_FEED_LAYOUT_LABELS = {
    image: 'Image only',
    'image-button': 'Image + button',
};
const FEED_FIELDS = ['id', 'type', 'layout', 'legend', 'title', 'detail', 'button', 'link', 'videoId', 'image', 'start', 'end'];
const FEED_FIELD_SET = new Set(FEED_FIELDS);
const HTTP = /^https?:\/\/\S+$/i; // the app's isHttp()
const PICTURE = /^[^/]+\.(jpg|jpeg|png|webp)$/;
const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
// The ECMAScript date-time format: the one shape Date.parse reads the same way on every engine.
const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})?)?$/;
const isText = (v) => typeof v === 'string' && v.trim() !== '';
/** A promo sells a product, so its button has to go somewhere. */
const NEEDS_LINK = ['promo'];
/** A video's button opens the in-app player. */
const NEEDS_VIDEO = ['video'];
/**
 * Whether this card shows a button. A feature, release or notice with no link
 * is text only: no link, no button (SYNCFX_WEB_SERVER #16). The "image" layout
 * draws no button at all (#23).
 */
export const syncfxCardHasButton = (type, link, layout) => layout !== 'image' && (NEEDS_LINK.includes(type) || NEEDS_VIDEO.includes(type) || isText(link));
export const isSyncfxImageLayout = (layout) => SYNCFX_FEED_LAYOUTS.includes(layout);
/** A filename nginx serves from app/ as a picture. */
export const isSyncfxPictureName = (name) => PICTURE.test(name);
export const isYoutubeId = (v) => typeof v === 'string' && YOUTUBE_ID.test(v);
/** The instant a start or end names, or null when the app could not read it. */
function when(v) {
    if (typeof v !== 'string' || !ISO.test(v))
        return null;
    const [y, m, d] = v.slice(0, 10).split('-').map(Number);
    const day = new Date(Date.UTC(y, m - 1, d));
    // V8 reads 2026-02-30 as 2 March; check-feed (and Python) refuse it.
    if (day.getUTCMonth() !== m - 1 || day.getUTCDate() !== d)
        return null;
    // A time with no offset is phone-local in the app. Read it as UTC, as check-feed
    // does: it is only ever compared with the item's other date.
    const zoned = v.length === 10 || /(Z|[+-]\d{2}:\d{2})$/.test(v) ? v : `${v}Z`;
    const t = Date.parse(zoned);
    return Number.isNaN(t) ? null : t;
}
/**
 * Why the app would drop or misread each feed item, as check-feed reports it.
 * `pictures` is every filename in app/ once the publish lands: what is on main
 * plus any picture being uploaded with it.
 */
export function syncfxFeedProblems(feed, pictures) {
    if (!Array.isArray(feed))
        return [{ index: -1, field: null, message: 'The feed must be a list' }];
    const out = [];
    const ids = new Set();
    feed.forEach((raw, index) => {
        const add = (field, message) => out.push({ index, field, message });
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            add(null, 'Not an item');
            return;
        }
        const item = raw;
        for (const k of Object.keys(item)) {
            if (!FEED_FIELD_SET.has(k))
                add(k, `Unknown field "${k}" (a typo? the app ignores it)`);
        }
        for (const k of ['id', 'title']) {
            if (!isText(item[k]))
                add(k, 'Required');
        }
        if (isText(item.id)) {
            // The app remembers dismissals by id, so a reused id hides the new item
            // from everyone who dismissed the old one.
            if (ids.has(item.id))
                add('id', 'This id is used twice');
            ids.add(item.id);
        }
        for (const k of ['legend', 'detail']) {
            if (k in item && !isText(item[k]))
                add(k, 'Must be text');
        }
        const kind = item.type;
        if (!SYNCFX_FEED_TYPES.includes(kind))
            add('type', `Must be one of ${SYNCFX_FEED_TYPES.join(', ')}`);
        const layout = item.layout;
        if ('layout' in item && !isSyncfxImageLayout(layout))
            add('layout', `Must be one of ${SYNCFX_FEED_LAYOUTS.join(', ')}, or left out`);
        if (isSyncfxImageLayout(layout) && !('image' in item))
            add('image', `${SYNCFX_FEED_LAYOUT_LABELS[layout]} needs a picture`);
        if (layout === 'image-button' && !('link' in item) && !NEEDS_VIDEO.includes(kind)) {
            add('link', 'Image + button needs a link for its button');
        }
        if (kind === 'video' && !isYoutubeId(item.videoId))
            add('videoId', 'Needs the 11-character YouTube id, not the URL');
        if ('link' in item) {
            if (!(typeof item.link === 'string' && HTTP.test(item.link)))
                add('link', 'Must be a link starting https:// or http://');
        }
        else if (NEEDS_LINK.includes(kind)) {
            add('link', `A ${String(kind)} needs a link`);
        }
        // A button needs somewhere to go. Without one the card is text only. The
        // "image" layout draws no button, so there one is optional and ignored.
        if ('link' in item || NEEDS_LINK.includes(kind) || NEEDS_VIDEO.includes(kind)) {
            if (!isText(item.button) && layout !== 'image')
                add('button', 'Required');
        }
        else if ('button' in item) {
            add('button', 'Nothing for the button to open: add a link, or remove the button for a text-only card');
        }
        if ('image' in item) {
            if (!(typeof item.image === 'string' && HTTP.test(item.image)))
                add('image', 'Must be a link starting https:// or http://');
            else if (item.image.startsWith(SYNCFX_APP_BASE_URL)) {
                const name = item.image.slice(SYNCFX_APP_BASE_URL.length);
                if (!isSyncfxPictureName(name) || !pictures.includes(name))
                    add('image', `${name} is not a .jpg, .png or .webp in app/`);
            }
        }
        const start = when(item.start);
        const end = when(item.end);
        if ('start' in item && start === null)
            add('start', 'Use a date like 2026-11-01 or 2026-11-01T09:00:00Z');
        if ('end' in item && end === null)
            add('end', 'Use a date like 2026-11-01 or 2026-11-01T09:00:00Z');
        if (start !== null && end !== null && end <= start)
            add('end', 'Must be after the start, or the card never shows');
    });
    return out;
}
/** Why the app's Help screen would come up empty. Unlike the feed, one bad entry empties all of it. */
export function syncfxHelpProblems(videos) {
    if (!Array.isArray(videos))
        return [{ index: -1, field: null, message: 'The video list must be a list' }];
    const out = [];
    const ids = new Set();
    videos.forEach((raw, index) => {
        const add = (field, message) => out.push({ index, field, message });
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
            add(null, 'Not a video');
            return;
        }
        const v = raw;
        if (!isYoutubeId(v.id))
            add('id', 'Needs the 11-character YouTube id, not the URL');
        else if (ids.has(v.id))
            add('id', 'This video is listed twice');
        else
            ids.add(v.id);
        if (!isText(v.title))
            add('title', 'Required');
        for (const k of Object.keys(v)) {
            if (k !== 'id' && k !== 'title')
                add(k, `Unknown field "${k}" (a typo? the app ignores it)`);
        }
    });
    return out;
}
/**
 * A feed item as it should be written: text trimmed, empty optional fields
 * dropped (the app reads `"legend": ""` as a bad legend, not as none), a
 * non-video's videoId dropped, and keys in the README's order.
 */
export function cleanSyncfxFeedItem(item) {
    const out = {};
    const source = item;
    for (const k of FEED_FIELDS) {
        const v = typeof source[k] === 'string' ? source[k].trim() : source[k];
        if (v === undefined || v === '')
            continue;
        if (k === 'videoId' && item.type !== 'video')
            continue;
        out[k] = v;
    }
    // Fields this file doesn't know survive, so check-feed can name them instead of ATLAS hiding them.
    for (const k of Object.keys(source))
        if (!FEED_FIELD_SET.has(k))
            out[k] = source[k];
    return out;
}
/** The 11-character id from a YouTube link (watch, youtu.be, shorts, embed, live, Studio) or a bare id. */
/** Where every YouTube thumbnail lives; the page's CSP img-src allows it. */
export const YOUTUBE_THUMBNAIL_HOST = 'https://i.ytimg.com/vi/';
/**
 * YouTube's thumbnail of a video. maxresdefault (1280x720) is the sharpest but
 * missing on some older videos; hqdefault (480x360, letterboxed) always exists,
 * and the card's crop hides its bars. The app can't fall back by itself: a
 * broken picture URL shows a plain dark card.
 */
export const youtubeThumbnailUrl = (videoId, size) => `${YOUTUBE_THUMBNAIL_HOST}${videoId}/${size}.jpg`;
/** Whether an item's picture is that video's own YouTube thumbnail. */
export const isYoutubeThumbnailOf = (image, videoId) => !!image && isYoutubeId(videoId) && image.startsWith(`${YOUTUBE_THUMBNAIL_HOST}${videoId}/`);
export function youtubeIdFrom(input) {
    const s = input.trim();
    if (isYoutubeId(s))
        return s;
    let url;
    try {
        url = new URL(s);
    }
    catch {
        return null;
    }
    const host = url.hostname.toLowerCase().replace(/^(www|m|music)\./, '');
    let id = null;
    if (host === 'youtu.be')
        id = url.pathname.split('/')[1];
    else if (host === 'studio.youtube.com')
        id = url.pathname.match(/^\/video\/([^/]+)/)?.[1];
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
        id = url.searchParams.get('v') ?? url.pathname.match(/^\/(?:shorts|embed|live|v)\/([^/]+)/)?.[1];
    }
    return isYoutubeId(id) ? id : null;
}
const slug = (text) => text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 40)
    .replace(/^-+|-+$/g, '') || 'item';
/** A first unused name of the form base, base-2, base-3... */
function unused(base, suffix, taken) {
    if (!taken.has(`${base}${suffix}`))
        return `${base}${suffix}`;
    let n = 2;
    while (taken.has(`${base}-${n}${suffix}`))
        n += 1;
    return `${base}-${n}${suffix}`;
}
/**
 * A new feed item's id, e.g. promo-gigbar-move-2026-10. The month keeps a later
 * item with the same title from reusing an id that is gone from the file, which
 * phones that dismissed the old one would still remember.
 */
export function newSyncfxFeedItemId(type, title, taken, now = new Date()) {
    return unused(`${type}-${slug(title)}-${now.toISOString().slice(0, 7)}`, '', new Set(taken));
}
/** A new picture's filename. Never one already in app/: phones keep a picture for a day by its URL. */
export function newSyncfxPictureName(itemId, taken) {
    return unused(slug(itemId), '.jpg', new Set(taken));
}
//# sourceMappingURL=syncfx-content.js.map